'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowLeft, X } from 'lucide-react'
import BriefLoginForm from '@/components/brief613/LoginForm'
import BriefRegisterForm from '@/components/brief613/RegisterForm'
import BriefClientDashboard from '@/components/brief613/ClientDashboard'
import BriefAdminDashboard from '@/components/brief613/AdminDashboard'
import ManagerLoginForm from '@/components/brief613/ManagerLoginForm'
import ManagerDashboard from '@/components/brief613/ManagerDashboard'
import LanguageSwitcher from '@/components/brief613/LanguageSwitcher'
import { Language } from '@/lib/brief613/translations'
import { useBrief613Translation } from '@/hooks/useBrief613Translation'
import { syncPortalSso } from '@/lib/portal-sso-client'
import {
  pageShell,
  headingClass,
  ghostBtn,
} from '@/components/brief613/ui'

type Section = 'login' | 'register' | 'client-dashboard' | 'admin-dashboard' | 'manager-login' | 'manager-dashboard'

export default function Brief613Page() {
  const [currentSection, setCurrentSection] = useState<Section>('login')
  const [language, setLanguage] = useState<Language>('en')
  const [user, setUser] = useState<{
    id: string
    username: string
    email: string
    role: string
    companyName?: string | null
  } | null>(null)
  const [manager, setManager] = useState<{
    id: string
    name: string
    pronom: string | null
    email: string
    phone: string | null
    photoUrl: string | null
    username: string
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const t = useBrief613Translation(language)

  useEffect(() => {
    const savedLang = localStorage.getItem('brief613_language') as Language
    if (savedLang && ['en', 'fr', 'ar', 'ru', 'zh'].includes(savedLang)) {
      setLanguage(savedLang)
    }

    fetch('/api/brief613/init', { method: 'POST' })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          console.log('[Brief613] Default admin initialized')
        }
      })
      .catch((err) => {
        console.error('[Brief613] Error initializing admin:', err)
      })

    const applyStored = () => {
      const storedUserId = localStorage.getItem('brief613_userId')
      const storedUserRole = localStorage.getItem('brief613_userRole')
      const storedUsername = localStorage.getItem('brief613_username')
      const storedManagerId = localStorage.getItem('brief613_managerId')

      if (storedManagerId) {
        fetch(`/api/brief613/managers/${storedManagerId}`, {
          headers: {
            'x-user-id': storedManagerId,
            'x-user-role': 'manager',
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.success) {
              setManager(data.manager)
              setCurrentSection('manager-dashboard')
            }
          })
          .catch(() => {
            localStorage.removeItem('brief613_managerId')
          })
          .finally(() => setLoading(false))
      } else if (storedUserId && storedUserRole) {
        if (storedUserRole === 'admin') {
          setUser({
            id: storedUserId,
            username: storedUsername || '',
            email: '',
            role: 'admin',
          })
          setCurrentSection('admin-dashboard')
        } else {
          setUser({
            id: storedUserId,
            username: storedUsername || '',
            email: '',
            role: storedUserRole,
          })
          setCurrentSection('client-dashboard')
        }
        setLoading(false)
      } else {
        setLoading(false)
      }
    }

    syncPortalSso().finally(applyStored)
  }, [])

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang)
    localStorage.setItem('brief613_language', lang)
  }

  const handleLogin = (userData: {
    id: string
    username: string
    email: string
    role: string
    companyName?: string | null
  }) => {
    setUser(userData)
    localStorage.setItem('brief613_userId', userData.id)
    localStorage.setItem('brief613_userRole', userData.role)
    localStorage.setItem('brief613_username', userData.username)
    if (userData.companyName) {
      localStorage.setItem('brief613_companyName', userData.companyName)
    }
    setCurrentSection(userData.role === 'admin' ? 'admin-dashboard' : 'client-dashboard')
  }

  const handleManagerLogin = (managerData: {
    id: string
    name: string
    pronom: string | null
    email: string
    phone: string | null
    photoUrl: string | null
    username: string
  }) => {
    setManager(managerData)
    localStorage.setItem('brief613_managerId', managerData.id)
    setCurrentSection('manager-dashboard')
  }

  const handleLogout = () => {
    setUser(null)
    setManager(null)
    localStorage.removeItem('brief613_userId')
    localStorage.removeItem('brief613_userRole')
    localStorage.removeItem('brief613_username')
    localStorage.removeItem('brief613_companyName')
    localStorage.removeItem('brief613_managerId')
    setCurrentSection('login')
  }

  if (loading) {
    return (
      <div className={`${pageShell} flex items-center justify-center`}>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/80" />
      </div>
    )
  }

  return (
    <main className={pageShell}>
      <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-8">
        <header className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <Link
              href="/"
              className="shrink-0 rounded-md opacity-90 transition-opacity hover:opacity-100"
              aria-label="Retour au portail"
            >
              <Image
                src="/logos/logo%20white.png"
                alt="613"
                width={117}
                height={28}
                className="h-7 w-auto object-contain"
                priority
              />
            </Link>
            <div className="min-w-0 border-l border-white/10 pl-4">
              <h1 className={headingClass}>{t.nav.title}</h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <LanguageSwitcher currentLanguage={language} onLanguageChange={handleLanguageChange} />
            {(user || manager) && (
              <>
                <span className="hidden text-xs text-white/45 sm:inline">
                  {t.nav.welcome}{' '}
                  {manager
                    ? `${manager.pronom ? manager.pronom + ' ' : ''}${manager.name}`
                    : user?.username}
                </span>
                <button type="button" onClick={handleLogout} className={ghostBtn}>
                  <X className="h-3.5 w-3.5" />
                  {t.nav.logout}
                </button>
              </>
            )}
            {!user && !manager && (
              <button
                type="button"
                onClick={() => setCurrentSection('manager-login')}
                className={ghostBtn}
              >
                {t.nav.managerLogin}
              </button>
            )}
            <Link href="/" className={ghostBtn}>
              <ArrowLeft className="h-3.5 w-3.5" />
              Portail
            </Link>
          </div>
        </header>

        <div className="py-2">
          {currentSection === 'login' && !user && (
            <BriefLoginForm
              onSuccess={handleLogin}
              onSwitchToRegister={() => setCurrentSection('register')}
              language={language}
            />
          )}

          {currentSection === 'register' && !user && (
            <BriefRegisterForm
              onSuccess={handleLogin}
              onSwitchToLogin={() => setCurrentSection('login')}
              language={language}
            />
          )}

          {currentSection === 'client-dashboard' && user && user.role === 'client' && (
            <BriefClientDashboard userId={user.id} language={language} />
          )}

          {currentSection === 'admin-dashboard' && user && user.role === 'admin' && (
            <BriefAdminDashboard userId={user.id} language={language} />
          )}

          {currentSection === 'manager-login' && !manager && (
            <ManagerLoginForm onSuccess={handleManagerLogin} language={language} />
          )}

          {currentSection === 'manager-dashboard' && manager && (
            <ManagerDashboard manager={manager} onLogout={handleLogout} language={language} />
          )}
        </div>
      </div>
    </main>
  )
}
